// TODO: add translations

/**
 * type FAQ = {
 *  title?: string
 *  titles?: string[],
 *  borderBottom?: boolean,
 *  questions: {
 *    question: string | undefined,
 *    answer: string,
 *    inline?: boolean
 *    options: {
 *    title: string,
 *    text: string,
 *    options: {
 *      title: string,
 *      text: string,
 *      }
 *    }[]
 *  }
 * }
 */

export const FAQ = [
  {
    title: 'About HomeAI.CH',
    questions: [
      {
        question: 'What is the HomeAi.CH service?',
        answer: 'It\'s a chat-based assistant designed to streamline your property search in Switzerland. After answering a few simple yet comprehensive questions, it instantly provides personalized property recommendations, clearly detailing why each option fits your specific requirements.'
      },
      {
        question: 'How much does the assistant cost?',
        answer: 'You can try it free for 7 days. After the trial, unlimited access costs CHF 99 per month. You may cancel anytime, with no long-term commitment.'
      },
      {
        question: 'Which areas in Switzerland are covered?',
        answer: 'The assistant covers all Swiss cantons and municipalities—from major cities like Zurich, Geneva, Lausanne, and Basel to smaller towns and rural areas. Listings are updated multiple times daily to ensure accuracy.'
      }
    ]
  },
  {
    title: 'Why the Assistant is Needed',
    questions: [
      {
        question: 'Why use HomeAI instead of traditional search methods?',
        answer: 'Searching for properties involves much more than just filtering by price or location. The AI Home-Search Assistant considers a broad spectrum of personal, practical, and financial factors simultaneously, providing benefits such as:',
        solution: 'The assistant helps you discover properties uniquely matched to your lifestyle that standard platforms might overlook.',
        options: [
          {
            title: 'Highly personalized recommendations',
            text: ' beyond basic filtering criteria.'
          },
          {
            title: 'Comprehensive understanding of your lifestyle',
            text: ', including commuting habits, family and social life, and essential amenities.'
          },
          {
            title: 'Detailed financial insights',
            text: ', accounting for total living costs, taxes, commuting expenses, and more.'
          },
          {
            title: 'Efficient search experience',
            text: ', saving you time by narrowing down truly suitable options.'
          }
        ]
      },
      {
        question: 'How does the assistant save me time compared to manual searches?',
        answer: 'The assistant instantly filters through thousands of listings daily, applying your detailed preferences and analyzing complex factors like total living costs, precise commute times, and lifestyle considerations. This significantly reduces the time you\'d spend manually searching and comparing properties.'
      },
      {
        question: 'How can the assistant help me avoid hidden costs?',
        answer: 'The assistant explicitly evaluates hidden or overlooked costs such as local taxes, commuting expenses, and utilities (heating, broadband availability, energy efficiency). You’ll receive transparent insights, preventing unexpected financial surprises.'
      },
      {
        question: 'How does the assistant improve decision-making accuracy?',
        answer: 'By considering over 60 detailed data points—including demographic data, environmental quality, local competition, transport accessibility, and detailed photo analysis—it ensures your choices are informed by comprehensive, accurate, and nuanced data, rather than guesswork or incomplete information.'
      },
      {
        question: 'How does the assistant ensure recommendations match my actual lifestyle?',
        answer: 'Recommendations go far beyond basic filters, incorporating personal and practical details about your family, daily routines, and preferred amenities. Whether it\'s proximity to favorite restaurants, fitness centers, or a safer route for your children\'s school commute, the assistant ensures each recommendation closely aligns with your actual day-to-day life.'
      },
      {
        question: 'Can the assistant uncover ideal locations I might not consider myself?',
        answer: 'Yes. By evaluating comprehensive commute patterns and total living costs rather than just geographic proximity, the assistant frequently recommends excellent properties in locations you might otherwise overlook, enhancing your living quality or financial efficiency.'
      },
      {
        question: 'How does the assistant continuously improve the quality of its recommendations?',
        answer: 'The assistant actively integrates your feedback on each suggestion, refining future recommendations. This interactive feedback loop ensures the search results become increasingly precise and personalized over time.'
      },
      {
        question: 'How detailed are the insights provided about each property?',
        answer: 'The assistant delivers thorough, actionable insights for every recommended property, clearly explaining why each home fits your needs—including detailed financial breakdowns, lifestyle suitability, commute efficiency, and environmental quality assessments.'
      }
    ]
  },
  {
    title: 'How HomeAI.ch Works',
    questions: [
      {
        question: 'Why is this assistant better than traditional property websites?',
        answer: 'Unlike standard property portals, which use basic filters (e.g., price, location, rooms), our assistant comprehensively considers detailed factors such as:\n- Total Living Costs\n- Commute and Transit\n- Detailed Photo Analysis\n- Quality-of-Life Indicators\n- Competition Analysis\n- Proximity to Personal Interests\n- Family Considerations\nThis comprehensive approach ensures you uncover ideal properties you\'d likely miss using conventional search methods.'
      },
      {
        question: 'How detailed is the interview process?',
        answer: 'The interview process replicates having a personal real estate assistant. It covers location preferences, property type and size, budget, living space needs, lifestyle and family considerations, commute preferences, and timelines.'
      },
      {
        question: 'How many data points does HomeAi.CH consider?',
        answer: 'The assistant evaluates over 60 unique data points tailored to your specific preferences, ensuring personalized and precise recommendations. These encompass financial, demographic, geographic, transportation, local amenities, environmental aspects, and personal lifestyle factors.'
      },
      {
        question: 'Can you provide a practical example?',
        answer: 'Yes. Instead of just using filters, the assistant conducts an interview to fully understand your lifestyle. Example: You need a 3-bedroom apartment near Zurich for CHF 3,000. It must be pet-friendly, have a modern kitchen and balcony, good air, childcare nearby, and access to good public schools. The assistant considers your commute frequency and transportation, local tax benefits, proximity and safety to childcare, environmental conditions, and property visuals. A recommendation might be an apartment in Küsnacht for CHF 3,200 with better tax offsets and perfect alignment with your criteria.'
      },
      {
        question: 'What types of questions will I need to answer?',
        answer: 'Only essential ones: your budget, location preferences, property type, move-in date, pet-related needs, commute habits, and amenities like a balcony or elevator.'
      }
    ]
  },
  {
    title: 'Personalization and Preferences',
    questions: [
      {
        question: 'Does the assistant remember my preferences?',
        answer: 'Yes. With an active subscription, it securely stores your preferences (budget, preferred areas, essential home features). Future searches automatically apply these preferences.'
      },
      {
        question: 'Can I get alerts for new matching properties?',
        answer: 'Absolutely. You instantly receive email notifications whenever new properties matching your criteria become available.'
      }
    ]
  },
  {
    title: 'Renting and Buying',
    questions: [
      {
        question: 'Does the assistant help both renters and buyers?',
        answer: 'Yes. Recommendations adapt based on whether you\'re renting or buying. For buyers, it further considers property taxes, year of construction, renovation potential, mortgage options, and long-term investment value.'
      }
    ]
  },
  {
    title: 'Privacy and Account Management',
    questions: [
      {
        question: 'How secure is my personal data?',
        answer: 'Your data and preferences are stored securely, kept strictly confidential, and never shared or sold. You can easily update or delete your information via account settings.'
      },
      {
        question: 'How do I cancel my subscription?',
        answer: 'Canceling is simple and hassle-free. Visit your account page, select "Billing," and click "Cancel Subscription." Your subscription remains active until the current billing period ends, with no further charges afterward.'
      }
    ]
  }
];
